import React, { useState, useEffect, useMemo } from 'react';
import CustomButton from './Button';
import { useAlert } from '../../providers/AlertProvider';
import { pickOrderWithQuantities, getOrderPickStatus } from '../../api/orders';
import logger from "../../utils/logger";


/**
 * EnhancedPickingModal.jsx
 * 
 * Enhanced modal component for order picking with quantity tracking and discrepancy reasons.
 * Supports partial picking where users can input actual picked quantities and reasons for discrepancies.
 */
export default function EnhancedPickingModal({ 
    order, 
    existingPickData = null,
    onComplete, 
    onCancel, 
    theme = "light" 
}) {
    const [pickedItems, setPickedItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pickStatus, setPickStatus] = useState(null);
    const { showAlert } = useAlert();

    const isDark = theme === "dark";
    const color = {
        bg: isDark ? "#1a1a1a" : "#ffffff",
        fg: isDark ? "#ffffff" : "#000000",
        border: isDark ? "#333" : "#ddd",
        borderAccent: isDark ? "#444" : "#ccc",
        modalBorder: isDark ? "#555" : "#bbb",
        headerBg: isDark ? "#2a2a2a" : "#f8f9fa",
        subtext: isDark ? "#aaa" : "#666",
        input: isDark ? "#2a2a2a" : "#ffffff",
        inputBorder: isDark ? "#444" : "#ddd",
        danger: "#dc3545",
        success: "#28a745",
        warning: "#ffc107"
    };

    // Initialize picked items from order items or existing pick data
    useEffect(() => {
        if (order && order.order_items) {
            const initialPickedItems = order.order_items.map(item => {
                // Find the corresponding detailed item for product information
                const detailedItem = order.detailed_items?.find(
                    detailed => detailed.product_id === item.product_id && detailed.pallet_id === item.pallet_id
                ) || {};
                
                // Check if there's existing pick data for this item
                const existingItem = existingPickData?.pickedItems?.find(
                    picked => picked.product_id === item.product_id && picked.pallet_id === item.pallet_id
                );
                
                return {
                    product_id: item.product_id,
                    pallet_id: item.pallet_id,
                    requested_quantity: item.requested_quantity,
                    available_quantity: item.available_quantity,
                    picked_quantity: existingItem ? existingItem.picked_quantity : item.requested_quantity, // Use existing or default to full pick
                    reason: existingItem ? existingItem.reason : '', // Use existing reason or empty initially
                    // Include detailed product information
                    product_name: detailedItem.product_name,
                    product_code: detailedItem.product_code,
                    batch_no: detailedItem.batch_no,
                    pallet_code: detailedItem.pallet_code,
                    lane_id: detailedItem.lane_id
                };
            });
            setPickedItems(initialPickedItems);
        }
    }, [order, existingPickData]);

    // Load existing pick status if order is already picked
    useEffect(() => {
        if (order && order.status === 'picked') {
            loadPickStatus();
        }
    }, [order]);

    const loadPickStatus = async () => {
        try {
            const result = await getOrderPickStatus(order.id);
            if (result.success && result.data.pick_status) {
                setPickStatus(result.data.pick_status);
                // Update picked items with existing data
                if (result.data.pick_status.picked_items) {
                    const existingPickedItems = result.data.pick_status.picked_items.map(item => ({
                        ...item,
                        reason: result.data.pick_status.discrepancy_reasons?.[`${item.product_id}-${item.pallet_id}`] || ''
                    }));
                    setPickedItems(existingPickedItems);
                }
            }
        } catch (error) {
            logger.error('Error loading pick status:', error);
        }
    };

    const updatePickedQuantity = (productId, palletId, quantity) => {
        setPickedItems(prev => prev.map(item => {
            if (item.product_id === productId && item.pallet_id === palletId) {
                return {
                    ...item,
                    picked_quantity: Math.max(0, Math.min(quantity, item.available_quantity))
                };
            }
            return item;
        }));
    };

    const updateReason = (productId, palletId, reason) => {
        setPickedItems(prev => prev.map(item => {
            if (item.product_id === productId && item.pallet_id === palletId) {
                return { ...item, reason };
            }
            return item;
        }));
    };

    const validatePicking = () => {
        const errors = [];

        for (const item of pickedItems) {
            const productName = item.product_name || `Product ${item.product_id.slice(0, 8)}...`;
            const palletCode = item.pallet_code || `Pallet ${item.pallet_id.slice(0, 8)}...`;
            
            if (item.picked_quantity < 0) {
                errors.push(`Picked quantity cannot be negative for ${productName} on ${palletCode}`);
            }
            if (item.picked_quantity > item.available_quantity) {
                errors.push(`Picked quantity (${item.picked_quantity}) exceeds available quantity (${item.available_quantity}) for ${productName} on ${palletCode}`);
            }
            if (item.picked_quantity > item.requested_quantity) {
                errors.push(`Picked quantity (${item.picked_quantity}) cannot exceed requested quantity (${item.requested_quantity}) for ${productName} on ${palletCode}`);
            }
            // Require reason for discrepancies
            if (item.picked_quantity !== item.requested_quantity && !item.reason.trim()) {
                errors.push(`Reason required for discrepancy on ${productName} on ${palletCode}`);
            }
        }

        return errors;
    };

    const handleSave = async () => {
        const validationErrors = validatePicking();
        if (validationErrors.length > 0) {
            showAlert({
                message: validationErrors.join('\n'),
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }]
            });
            return;
        }

        setLoading(true);
        try {
            // Store picked data locally instead of submitting to database
            const pickedData = {
                pickedItems: pickedItems,
                pickedAt: new Date().toISOString(),
                totalRequested: summary.totalRequested,
                totalPicked: summary.totalPicked,
                discrepancies: summary.discrepancies,
                completionPercentage: summary.completionPercentage
            };

            // Pass the picked data back to parent component
            onComplete?.(pickedData);
            
            showAlert({
                message: 'Pick complete, Add pallet processing to proceed',
                type: 'success',
                buttons: [{ label: 'Close', type: 'success' }]
            });
        } catch (error) {
            showAlert({
                message: 'An error occurred while saving picking data',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }]
            });
        } finally {
            setLoading(false);
        }
    };

    // Calculate summary statistics
    const summary = useMemo(() => {
        const totalRequested = pickedItems.reduce((sum, item) => sum + item.requested_quantity, 0);
        const totalPicked = pickedItems.reduce((sum, item) => sum + item.picked_quantity, 0);
        const discrepancies = pickedItems.filter(item => item.picked_quantity !== item.requested_quantity).length;
        
        return {
            totalRequested,
            totalPicked,
            discrepancies,
            completionPercentage: totalRequested > 0 ? Math.round((totalPicked / totalRequested) * 100) : 0
        };
    }, [pickedItems]);

    // Get product details for display
    const getProductDetails = async (productId) => {
        // This would typically fetch from your products API
        // For now, return placeholder data
        return {
            product_name: `Product ${productId.slice(0, 8)}...`,
            product_code: `CODE-${productId.slice(0, 4)}`,
            batch_no: 'BATCH-001'
        };
    };

    if (!order) return null;

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0, 0, 0, 0.75)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
                padding: "20px",
            }}
        >
            <div
                style={{
                    backgroundColor: color.bg,
                    borderRadius: 12,
                    width: "95%",
                    maxWidth: 1200,
                    maxHeight: "95vh",
                    overflow: "hidden",
                    boxShadow: isDark 
                        ? "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)" 
                        : "0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.1)",
                    border: `2px solid ${color.modalBorder}`,
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                }}
            >
                {/* Header */}
                <div
                    style={{
                        padding: "1.5rem",
                        borderBottom: `2px solid ${color.borderAccent}`,
                        backgroundColor: color.headerBg,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexShrink: 0,
                        position: "relative",
                        borderRadius: "12px 12px 0 0",
                    }}
                >
                    <div style={{ textAlign: "center" }}>
                        <h3 style={{ margin: 0, color: color.fg, fontSize: "1.25rem" }}>
                            Pick Order {order.order_number}
                        </h3>
                        <div style={{ color: color.subtext, fontSize: '0.9rem', marginTop: '0.5rem' }}>
                            {summary.totalPicked} of {summary.totalRequested} items • {summary.completionPercentage}% complete
                            {summary.discrepancies > 0 && (
                                <span style={{ color: color.warning, marginLeft: '1rem' }}>
                                    {summary.discrepancies} discrepancies
                                </span>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onCancel}
                        style={{
                            position: "absolute",
                            right: "1.5rem",
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "none",
                            border: `1px solid ${color.border}`,
                            fontSize: "1.5rem",
                            color: color.subtext,
                            cursor: "pointer",
                            padding: "0.5rem",
                            borderRadius: "8px",
                            transition: "all 0.2s ease",
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = isDark ? "#333" : "#f0f0f0";
                            e.target.style.color = color.fg;
                            e.target.style.borderColor = color.borderAccent;
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "transparent";
                            e.target.style.color = color.subtext;
                            e.target.style.borderColor = color.border;
                        }}
                    >
                        ✕
                    </button>
                </div>

                {/* Content */}
                <div style={{ 
                    padding: "1.5rem", 
                    flex: 1, 
                    overflowY: "auto",
                    minHeight: 0
                }}>
                    <div style={{ 
                        border: `2px solid ${color.borderAccent}`,
                        borderRadius: 8,
                        overflow: "hidden",
                        boxShadow: isDark 
                            ? "0 4px 12px rgba(0, 0, 0, 0.3)" 
                            : "0 4px 12px rgba(0, 0, 0, 0.1)",
                        width: "100%"
                    }}>
                        <div style={{ overflow: "auto", width: "100%" }}>
                            <table style={{ 
                                width: "100%", 
                                borderCollapse: "collapse",
                                tableLayout: "fixed",
                                margin: 0,
                                padding: 0,
                                display: "table"
                            }}>
                                <colgroup>
                                    <col style={{ width: "25%", minWidth: "25%", maxWidth: "25%" }} />
                                    <col style={{ width: "11%", minWidth: "11%", maxWidth: "11%" }} />
                                    <col style={{ width: "12%", minWidth: "12%", maxWidth: "12%" }} />
                                    <col style={{ width: "12%", minWidth: "12%", maxWidth: "12%" }} />
                                    <col style={{ width: "12%", minWidth: "12%", maxWidth: "12%" }} />
                                    <col style={{ width: "28%", minWidth: "28%", maxWidth: "28%" }} />
                                </colgroup>
                            <thead>
                                <tr style={{ backgroundColor: color.headerBg }}>
                                    <th style={{ 
                                        padding: '1rem', 
                                        textAlign: 'left', 
                                        borderBottom: `1px solid ${color.border}`, 
                                        color: color.fg,
                                        fontSize: "0.9rem",
                                        fontWeight: "600"
                                    }}>
                                        Product Details
                                    </th>
                                    <th style={{ 
                                        padding: '1rem', 
                                        textAlign: 'left', 
                                        borderBottom: `1px solid ${color.border}`, 
                                        color: color.fg,
                                        fontSize: "0.9rem",
                                        fontWeight: "600"
                                    }}>
                                        Location
                                    </th>
                                    <th style={{ 
                                        padding: '1rem', 
                                        textAlign: 'center', 
                                        borderBottom: `1px solid ${color.border}`, 
                                        color: color.fg,
                                        fontSize: "0.9rem",
                                        fontWeight: "600"
                                    }}>
                                        Requested
                                    </th>
                                    <th style={{ 
                                        padding: '1rem', 
                                        textAlign: 'center', 
                                        borderBottom: `1px solid ${color.border}`, 
                                        color: color.fg,
                                        fontSize: "0.9rem",
                                        fontWeight: "600"
                                    }}>
                                        Available
                                    </th>
                                    <th style={{ 
                                        padding: '1rem', 
                                        textAlign: 'center', 
                                        borderBottom: `1px solid ${color.border}`, 
                                        color: color.fg,
                                        fontSize: "0.9rem",
                                        fontWeight: "600"
                                    }}>
                                        Picked
                                    </th>
                                    <th style={{ 
                                        padding: '1rem', 
                                        textAlign: 'center', 
                                        borderBottom: `1px solid ${color.border}`, 
                                        color: color.fg,
                                        fontSize: "0.9rem",
                                        fontWeight: "600"
                                    }}>
                                        Reason (if discrepancy)
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {pickedItems.map((item, index) => {
                                    const hasDiscrepancy = item.picked_quantity !== item.requested_quantity;
                                    const rowStyle = {
                                        backgroundColor: hasDiscrepancy ? 
                                            (isDark ? "#2a1f1f" : "#fff5f5") : 
                                            (index % 2 === 0 ? color.bg : color.headerBg)
                                    };

                                    return (
                                        <tr key={`${item.product_id}-${item.pallet_id}`} style={rowStyle}>
                                            <td style={{ 
                                                padding: '1rem', 
                                                borderBottom: `1px solid ${color.border}`,
                                                color: color.fg,
                                                verticalAlign: "top",
                                                textAlign: "left"
                                            }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', textAlign: "left" }}>
                                                    <div style={{ fontWeight: 'bold', fontSize: '1rem', lineHeight: '1.2', textAlign: "left" }}>
                                                        {item.product_name || `Product ${item.product_id.slice(0, 8)}...`}
                                                    </div>
                                                    <div style={{ fontSize: '0.85rem', color: color.subtext, textAlign: "left" }}>
                                                        Code: {item.product_code || 'N/A'}
                                                    </div>
                                                    {item.batch_no && (
                                                        <div style={{ fontSize: '0.85rem', color: color.subtext, textAlign: "left" }}>
                                                            Batch: {item.batch_no}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ 
                                                padding: '1rem', 
                                                borderBottom: `1px solid ${color.border}`,
                                                color: color.fg,
                                                verticalAlign: "top",
                                                textAlign: "left"
                                            }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', textAlign: "left" }}>
                                                    <div style={{ fontSize: '1rem', fontWeight: 'bold', lineHeight: '1.2', textAlign: "left" }}>
                                                        {item.pallet_code || `Pallet ${item.pallet_id.slice(0, 8)}...`}
                                                    </div>
                                                    {item.lane_id && (
                                                        <div style={{ fontSize: '0.85rem', color: color.subtext, textAlign: "left" }}>
                                                            Lane: {item.lane_id}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ 
                                                padding: '1rem', 
                                                borderBottom: `1px solid ${color.border}`,
                                                color: color.fg,
                                                textAlign: 'center',
                                                verticalAlign: "middle",
                                                fontSize: "1rem",
                                                fontWeight: "600"
                                            }}>
                                                {item.requested_quantity}
                                            </td>
                                            <td style={{ 
                                                padding: '1rem', 
                                                borderBottom: `1px solid ${color.border}`,
                                                color: color.subtext,
                                                textAlign: 'center',
                                                verticalAlign: "middle",
                                                fontSize: "1rem"
                                            }}>
                                                {item.available_quantity}
                                            </td>
                                            <td style={{ 
                                                padding: '1rem', 
                                                borderBottom: `1px solid ${color.border}`,
                                                textAlign: 'center',
                                                verticalAlign: "middle"
                                            }}>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max={item.requested_quantity}
                                                    value={item.picked_quantity}
                                                    onChange={(e) => updatePickedQuantity(
                                                        item.product_id, 
                                                        item.pallet_id, 
                                                        parseInt(e.target.value) || 0
                                                    )}
                                                    disabled={loading || pickStatus}
                                                    style={{
                                                        width: '90px',
                                                        padding: '0.75rem',
                                                        borderRadius: 6,
                                                        border: `2px solid ${hasDiscrepancy ? color.warning : color.inputBorder}`,
                                                        backgroundColor: color.input,
                                                        color: color.fg,
                                                        textAlign: 'center',
                                                        fontSize: '1rem',
                                                        fontWeight: '600'
                                                    }}
                                                />
                                            </td>
                                            <td style={{ 
                                                padding: '1rem', 
                                                borderBottom: `1px solid ${color.border}`,
                                                verticalAlign: "middle",
                                                textAlign: "center"
                                            }}>
                                                {hasDiscrepancy ? (
                                                    <input
                                                        type="text"
                                                        placeholder="Enter reason for discrepancy"
                                                        value={item.reason}
                                                        onChange={(e) => updateReason(
                                                            item.product_id, 
                                                            item.pallet_id, 
                                                            e.target.value
                                                        )}
                                                        disabled={loading || pickStatus}
                                                        style={{
                                                            width: '100%',
                                                            padding: '0.75rem',
                                                            borderRadius: 6,
                                                            border: `2px solid ${!item.reason.trim() ? color.danger : color.inputBorder}`,
                                                            backgroundColor: color.input,
                                                            color: color.fg,
                                                            fontSize: '0.9rem'
                                                        }}
                                                    />
                                                ) : (
                                                    <span style={{ color: color.subtext, fontStyle: 'italic', fontSize: '0.9rem' }}>
                                                        No discrepancy
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div
                    style={{
                        padding: "1.5rem",
                        borderTop: `2px solid ${color.borderAccent}`,
                        backgroundColor: color.headerBg,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexShrink: 0,
                        borderRadius: "0 0 12px 12px",
                    }}
                >
                    <div style={{ color: color.subtext, fontSize: '0.9rem' }}>
                        {summary.discrepancies > 0 && 
                            `${summary.discrepancies} items require reasons for discrepancies`
                        }
                    </div>
                    <div style={{ display: "flex", gap: "1rem" }}>
                        <CustomButton
                            onClick={onCancel}
                            variant="secondary"
                            theme={theme}
                            disabled={loading}
                            style={{ 
                                minWidth: "100px",
                                padding: "0.75rem 1.5rem",
                                fontSize: "0.9rem",
                                fontWeight: "600"
                            }}
                        >
                            Cancel
                        </CustomButton>
                        {!pickStatus && (
                            <CustomButton
                                onClick={handleSave}
                                variant="primary"
                                theme={theme}
                                disabled={loading}
                                style={{ 
                                    minWidth: "140px",
                                    padding: "0.75rem 1.5rem",
                                    fontSize: "0.9rem",
                                    fontWeight: "600"
                                }}
                            >
                                {loading ? "Picking..." : "Complete Pick"}
                            </CustomButton>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}